import { type PDFDocumentProxy } from './pdfjs';
import { createCanvas } from '../canvas/create-canvas';

export interface PdfPageToCanvasOptions {
  background?: string | CanvasGradient | CanvasPattern;
  canvasContext?: CanvasRenderingContext2D;
  pageNumber: number;
  pdf: PDFDocumentProxy;
}

/**
 * @param pageNumber The page number to get. The first page is 1.
 */
export async function pdfPageToCanvas(
  options: PdfPageToCanvasOptions
): Promise<CanvasRenderingContext2D | undefined> {
  if (options === null || typeof options !== 'object') {
    return undefined;
  }

  const { background, pageNumber, pdf } = options;
  let { canvasContext } = options;

  if (pdf === null || typeof pdf !== 'object') {
    return undefined;
  }

  try {
    const page = await pdf.getPage(pageNumber);
    if (!page) {
      console.log(`Invalid PDF Page #${pageNumber}.`);

      return undefined;
    }

    const viewport = page.getViewport({ scale: 1.0 });
    if (!viewport) {
      console.log('Could not get PDF page\'s viewport.');

      return undefined;
    }

    if (!canvasContext) {
      canvasContext = createCanvas(viewport.width, viewport.height)?.context;
      if (!canvasContext) {
        console.log('Could not create canvas for PDF rendering.');

        return undefined;
      }
    }

    const task = page.render({
      background,
      canvasContext,
      viewport
    });
    if (!task) {
      console.log('Could not render PDF viewport to canvas.');

      return undefined;
    }

    await task.promise;

    page.cleanup();

    return canvasContext;
  } catch (e) {
    console.log(e instanceof Error ? e.message : e);
  }

  return undefined;
}
